import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ButtonComponent } from 'nebula-ui-kit';
import { catchError, forkJoin, of } from 'rxjs';
import { PreservationTraceService } from '../../../../../data-access/src/lib/ingest/preservation-trace.service';
import {
  PreservationTraceResponse,
  RecorridoResponse,
} from '../../../../../domain/src/lib/ingest/preservation-trace.model';

/**
 * Recorrido del archivo (trazabilidad): las tres partes (máster/consulta/
 * actualización), el timeline de pasos y los eventos PREMIS + fixity/antivirus.
 * Consume /api/preservation-trace/{ingestId}/recorrido y /{ingestId}.
 */
@Component({
  selector: 'app-preservation-trace',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  templateUrl: './preservation-trace.component.html',
  styleUrls: ['./preservation-trace.component.scss'],
})
export class PreservationTraceComponent implements OnInit {
  private readonly traceService = inject(PreservationTraceService);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly recorrido = signal<RecorridoResponse | null>(null);
  readonly trace = signal<PreservationTraceResponse | null>(null);

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('ingestId');
    const ingestId = Number(idParam);
    if (!idParam || Number.isNaN(ingestId)) {
      this.error.set('Identificador de expediente inválido.');
      this.loading.set(false);
      return;
    }

    forkJoin({
      recorrido: this.traceService.getRecorrido(ingestId).pipe(catchError(() => of(null))),
      trace: this.traceService.getTrace(ingestId).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ recorrido, trace }) => {
        if (!recorrido && !trace) {
          this.error.set('No se encontró el expediente o no tienes acceso.');
        }
        this.recorrido.set(recorrido);
        this.trace.set(trace);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Error consultando la trazabilidad.');
        this.loading.set(false);
      },
    });
  }

  goBack(): void {
    window.history.back();
  }

  /** Clase de badge según el outcome/estado (PASS/optimal vs FAIL/critical, etc.). */
  badgeClass(status: string | null | undefined): string {
    switch ((status ?? '').toLowerCase()) {
      case 'pass':
      case 'optimal':
      case 'preserved':
      case 'completed':
        return 'badge--ok';
      case 'fail':
      case 'critical':
      case 'quarantined':
      case 'error':
        return 'badge--fail';
      case 'attention':
        return 'badge--warn';
      default:
        return 'badge--muted';
    }
  }
}
